/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @material-ui core components
import React, { useContext, useMemo } from 'react'

import { Autocomplete, Checkbox, TextField } from '@mui/material'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'

import MDAvatar from 'components/MDAvatar'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import { ROLE_LEVEL } from 'constants/roles'
import { useAuth } from 'context/auth'
import { useMaterialUIController } from 'context/materialUI'
import { Feedback } from 'layouts/feedback/internal/types'
import FormField from 'modules/FormField'

import { FormContext } from '../../constants'

function CustomerOutcome(): JSX.Element {
	const [controller] = useMaterialUIController()
	const { user } = useAuth()
	const formContext = useContext(FormContext)
	const { localForm, setFormEdited, setLocalForm, showError } = formContext
	const { configuration } = controller

	const STAGE = 4

	const sectionDisabled = useMemo(
		() =>
			(!ROLE_LEVEL[2].includes(user.data.role) && localForm.stage.current !== STAGE) ||
			user.data.role === 'VIEWER',
		[localForm.stage, user.data.role]
	)

	const errorVisible = localForm.stage.current >= STAGE && showError

	const customerOutcomes = useMemo(
		() =>
			configuration?.feedback?.outcomes.filter(
				item => item.memberType.toUpperCase() === 'CUSTOMER' && item.feedbackType === 'COMPLAINT'
			) ?? [],
		[configuration?.feedback?.outcomes]
	)

	const outcomeNameToId = Object.fromEntries(customerOutcomes.map(o => [o.name, o.id.toString()]))

	const handleFormUpdate = (value: string, field: keyof Feedback['form']['customerOutcome']) => {
		setLocalForm(prev => ({
			...prev,
			form: {
				...prev.form,
				customerOutcome: {
					...prev.form.customerOutcome,
					[field]: value,
				},
			},
		}))
		setFormEdited(true)
	}

	return (
		<Card id={'customer-outcome'}>
			<MDBox display={'flex'} flexDirection={'row'} gap={2} p={3}>
				<MDAvatar bgColor="primary" size="xs">
					6
				</MDAvatar>
				<MDTypography variant="h5" fontWeight="medium">
					Customer Outcome
				</MDTypography>
			</MDBox>
			<MDBox component="form" pb={3} px={3}>
				<Grid container spacing={3}>
					<Grid item xs={12} sm={6} md={4}>
						<FormField
							disabled={sectionDisabled}
							variant="outlined"
							label="Date/Time"
							inputProps={{ type: 'datetime-local' }}
							value={localForm.form.customerOutcome?.datetime ?? ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'datetime')
							}}
							error={errorVisible && !localForm.form.customerOutcome?.datetime}
							helperText={
								errorVisible && !localForm.form.customerOutcome?.datetime
									? 'Date/Time is required'
									: ''
							}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Autocomplete
							disableClearable
							readOnly={sectionDisabled}
							value={localForm.form.customerOutcome?.outcome?.name ?? null}
							options={customerOutcomes?.map(item => item.name) ?? []}
							noOptionsText="No outcomes set for this member"
							onChange={(_, newValue) => {
								const id = outcomeNameToId[newValue] ?? ''
								setLocalForm(prev => ({
									...prev,
									form: {
										...prev.form,
										customerOutcome: {
											...prev.form.customerOutcome,
											outcome: {
												id,
												name: newValue,
											},
										},
									},
								}))
							}}
							size="medium"
							renderInput={params => (
								<FormField
									{...params}
									label="Outcome"
									InputLabelProps={{ shrink: true }}
									error={errorVisible && !localForm.form.customerOutcome?.outcome}
									helperText={
										errorVisible && !localForm.form.customerOutcome?.outcome
											? 'Outcome is required'
											: ''
									}
								/>
							)}
						/>
					</Grid>
					{customerOutcomes.find(o => o.name === localForm.form.customerOutcome?.outcome?.name)
						?.has_monetary_value && (
						<Grid item xs={12} sm={4}>
							<FormField
								label="Monetary Value (£)"
								placeholder="15.52"
								autoComplete="off"
								disabled={sectionDisabled}
								value={localForm.form?.customerOutcome?.monetary_value?.toString() ?? ''}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
									handleFormUpdate(e.target.value, 'monetary_value')
								}}
								error={errorVisible && !localForm.form.customerOutcome?.monetary_value}
								helperText={
									errorVisible && !localForm.form.customerOutcome.monetary_value
										? 'Monetary value is required'
										: ''
								}
							/>
						</Grid>
					)}
					<Grid item xs={12}>
						<TextField
							label="Details Of Outcome"
							disabled={sectionDisabled}
							multiline
							rows={5} // Adjust number of rows for the size of the textarea
							variant="outlined"
							placeholder="Please provide a description of the outcome."
							InputLabelProps={{ shrink: true }}
							fullWidth
							value={localForm.form.customerOutcome?.details ?? ''}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'details')
							}}
							error={errorVisible && !localForm.form.customerOutcome?.details}
							helperText={
								errorVisible && !localForm.form.customerOutcome?.details ? 'Details required' : ''
							}
						/>
					</Grid>
					<Grid item xs={12} sm={8}>
						{/* Left Side Content (optional) */}
					</Grid>
					<Grid item xs={12} sm={4}>
						<MDBox
							display="flex"
							alignItems="center"
							justifyContent={'flex-end'}
							flexDirection={'row'}
						>
							<MDTypography variant="h6" color="secondary">
								Customer Outcome Complete:
							</MDTypography>
							<Checkbox
								checked={localForm.stage.current > STAGE}
								disabled={localForm.stage.min > STAGE}
								onChange={() => {
									setLocalForm(prev => ({
										...prev,
										stage: {
											...prev.stage,
											current: localForm.stage.current > STAGE ? STAGE : STAGE + 1,
										},
									}))
									setFormEdited(true)
								}}
							/>
						</MDBox>
					</Grid>
				</Grid>
			</MDBox>
		</Card>
	)
}

export default CustomerOutcome
